import React, { useState, useEffect, useCallback } from 'react';
import { userService } from '../services/userService';
import { useAuth } from '../contexts/AuthContext';

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Reset password modal state
  const [resetTarget, setResetTarget] = useState(null); // { id, username }
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [usersData, settingData] = await Promise.all([
        userService.listUsers(),
        userService.getRegistrationSetting(),
      ]);
      setUsers(usersData);
      setRegistrationEnabled(settingData.value === 'true');
    } catch {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const flash = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleAction = async (actionFn, userId, successMessage) => {
    setActionLoading(userId + actionFn.name);
    setError('');
    try {
      const updated = await actionFn(userId);
      setUsers((prev) => prev.map((u) => (u.id === userId ? updated : u)));
      flash(successMessage);
    } catch (err) {
      setError(err.response?.data?.detail || 'Action failed.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleRegistration = async () => {
    setActionLoading('registration');
    setError('');
    try {
      const result = await userService.setRegistrationSetting(!registrationEnabled);
      setRegistrationEnabled(result.value === 'true');
      flash(`Registration ${result.value === 'true' ? 'enabled' : 'disabled'}.`);
    } catch {
      setError('Failed to update registration setting.');
    } finally {
      setActionLoading(null);
    }
  };

  const openResetModal = (u) => {
    setResetTarget(u);
    setResetPassword('');
    setResetConfirm('');
    setResetError('');
  };

  const closeResetModal = () => {
    setResetTarget(null);
  };

  const handleAdminResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');

    if (resetPassword !== resetConfirm) {
      setResetError('Passwords do not match.');
      return;
    }

    setResetLoading(true);
    try {
      await userService.adminSetPassword(resetTarget.id, resetPassword);
      flash(`Password reset for ${resetTarget.username}.`);
      closeResetModal();
    } catch (err) {
      setResetError(err.response?.data?.detail || 'Failed to reset password.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete ${u.username}? This cannot be undone.`)) return;
    setActionLoading(u.id + 'delete');
    setError('');
    try {
      await userService.deleteUser(u.id);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      flash(`${u.username} deleted.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete user.');
    } finally {
      setActionLoading(null);
    }
  };

  const inactiveUsers = users.filter((u) => !u.is_active);
  const activeUsers = users.filter((u) => u.is_active);

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center mt-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="bi bi-people me-2"></i>User Management
        </h2>
        <div className="d-flex align-items-center gap-3">
          <span className="text-muted small">Public Registration:</span>
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="registrationToggle"
              checked={registrationEnabled}
              onChange={handleToggleRegistration}
              disabled={actionLoading === 'registration'}
              style={{ cursor: 'pointer', width: '3em', height: '1.5em' }}
            />
            <label
              className={`form-check-label fw-semibold ${registrationEnabled ? 'text-success' : 'text-danger'}`}
              htmlFor="registrationToggle"
            >
              {registrationEnabled ? 'Open' : 'Closed'}
            </label>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')} />
        </div>
      )}
      {successMsg && (
        <div className="alert alert-success" role="alert">
          {successMsg}
        </div>
      )}

      {/* Inactive / Pending Users */}
      {inactiveUsers.length > 0 && (
        <div className="card mb-4 border-warning">
          <div className="card-header bg-warning bg-opacity-25 d-flex align-items-center gap-2">
            <i className="bi bi-clock-history text-warning"></i>
            <strong>Inactive / Pending ({inactiveUsers.length})</strong>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Registered</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {inactiveUsers.map((u) => (
                    <tr key={u.id}>
                      <td className="fw-semibold">{u.username}</td>
                      <td>{u.email}</td>
                      <td className="text-muted small">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <button
                          className="btn btn-success btn-sm me-2"
                          onClick={() =>
                            handleAction(
                              userService.activateUser,
                              u.id,
                              `${u.username} enabled.`
                            )
                          }
                          disabled={!!actionLoading}
                        >
                          <i className="bi bi-check-circle me-1"></i>Enable
                        </button>
                        <button
                          className="btn btn-outline-secondary btn-sm me-2"
                          onClick={() => openResetModal(u)}
                          disabled={!!actionLoading}
                          title="Reset password"
                        >
                          <i className="bi bi-key me-1"></i>Reset Password
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(u)}
                          disabled={!!actionLoading}
                          title="Delete user"
                        >
                          <i className="bi bi-trash me-1"></i>Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Active Users */}
      <div className="card">
        <div className="card-header d-flex align-items-center gap-2">
          <i className="bi bi-person-check text-success"></i>
          <strong>Active Users ({activeUsers.length})</strong>
        </div>
        <div className="card-body p-0">
          {activeUsers.length === 0 ? (
            <p className="text-muted text-center py-4">No active users yet.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Registered</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activeUsers.map((u) => (
                    <tr key={u.id}>
                      <td className="fw-semibold">
                        {u.username}
                        {u.id === currentUser?.id && (
                          <span className="badge bg-secondary ms-2">You</span>
                        )}
                      </td>
                      <td>{u.email}</td>
                      <td>
                        {u.is_admin ? (
                          <span className="badge bg-primary">Admin</span>
                        ) : (
                          <span className="badge bg-success">User</span>
                        )}
                      </td>
                      <td className="text-muted small">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <button
                          className="btn btn-outline-secondary btn-sm me-2"
                          onClick={() => openResetModal(u)}
                          disabled={!!actionLoading}
                          title="Reset password"
                        >
                          <i className="bi bi-key me-1"></i>Reset Password
                        </button>
                        {u.id !== currentUser?.id && (
                          <>
                            {u.is_admin ? (
                              <button
                                className="btn btn-outline-secondary btn-sm me-2"
                                onClick={() =>
                                  handleAction(
                                    userService.demoteUser,
                                    u.id,
                                    `${u.username} demoted to user.`
                                  )
                                }
                                disabled={!!actionLoading}
                                title="Remove admin role"
                              >
                                <i className="bi bi-arrow-down-circle me-1"></i>Demote
                              </button>
                            ) : (
                              <button
                                className="btn btn-outline-primary btn-sm me-2"
                                onClick={() =>
                                  handleAction(
                                    userService.promoteUser,
                                    u.id,
                                    `${u.username} promoted to admin.`
                                  )
                                }
                                disabled={!!actionLoading}
                                title="Make admin"
                              >
                                <i className="bi bi-arrow-up-circle me-1"></i>Make Admin
                              </button>
                            )}
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() =>
                                handleAction(
                                  userService.deactivateUser,
                                  u.id,
                                  `${u.username} disabled.`
                                )
                              }
                              disabled={!!actionLoading}
                              title="Disable account"
                            >
                              <i className="bi bi-slash-circle me-1"></i>Disable
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Admin Reset Password Modal */}
      {resetTarget && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            onClick={(e) => { if (e.target === e.currentTarget) closeResetModal(); }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-key me-2"></i>Reset Password — {resetTarget.username}
                  </h5>
                  <button type="button" className="btn-close" onClick={closeResetModal} />
                </div>
                <form onSubmit={handleAdminResetPassword}>
                  <div className="modal-body">
                    {resetError && (
                      <div className="alert alert-danger">{resetError}</div>
                    )}
                    <div className="mb-3">
                      <label className="form-label">New Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={resetPassword}
                        onChange={(e) => setResetPassword(e.target.value)}
                        minLength={6}
                        required
                        autoFocus
                        autoComplete="new-password"
                      />
                    </div>
                    <div className="mb-1">
                      <label className="form-label">Confirm Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={resetConfirm}
                        onChange={(e) => setResetConfirm(e.target.value)}
                        minLength={6}
                        required
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={closeResetModal}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={resetLoading}>
                      {resetLoading ? 'Saving...' : 'Set Password'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
};

export default UsersPage;
